import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../context/AuthContext';

export function MicrosoftCallback() {
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  // A code is exchanged once: the effect runs again when the auth context re-renders (and twice
  // in development), and a second exchange of the same code fails, flashing an error screen
  // just before the redirect.
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;
    const processCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(`Erreur Microsoft: ${errorParam}`);
        setIsProcessing(false);
        return;
      }

      if (!code) {
        setError('Code d\'autorisation manquant');
        setIsProcessing(false);
        return;
      }

      try {
        // Appeler le callback backend pour échanger le code contre un token
        const response = await apiClient.instance.get(`/auth/ms/callback?code=${code}`);
        const { access_token, user } = response.data;

        // Sauvegarder le token et l'utilisateur dans localStorage (même pattern que login)
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('user', JSON.stringify(user));

        // Forcer la mise à jour du contexte d'authentification
        await checkAuth();

        // Rediriger vers le dashboard
        navigate('/staffing', { replace: true });
      } catch (err) {
        console.error('Erreur lors du callback Microsoft:', err);
        setError(
          err instanceof Error && 'response' in err && typeof err.response === 'object' && err.response && 'data' in err.response && typeof err.response.data === 'object' && err.response.data && 'detail' in err.response.data
            ? String(err.response.data.detail)
            : 'Erreur lors de l\'authentification Microsoft'
        );
      } finally {
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, navigate, checkAuth]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Connexion avec Microsoft en cours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur d'authentification</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return null;
}